import { Fragment, type CSSProperties, type ReactNode } from 'react';

import { labelClipWrapper, labelSpan } from './BarLabelOverlay.sty.js';
import { BAR_LABEL_MIN_SIZE, BAR_LABEL_PADDING } from './label-utils.js';
import type { TruncationMode } from '../../../../../typography/text-ellipsis/TextEllipsis.js';
import { charsFontWidth12pixelsSize400Map } from '../../../../core/components/text-ellipsis/chars-font-width-map.js';
import { truncateString } from '../../../../core/components/text-ellipsis/truncate-string.js';

interface HorizontalBarLabelProps {
  x: number;
  y: number;
  barWidth: number;
  barHeight: number;
  canvasWidth: number;
  isReversed: boolean;
  text: string;
  insideColor: string;
  outsideColor: string;
  truncationMode: TruncationMode;
}

/**
 * HTML "split-clip" label for a single horizontal bar.
 *
 * The label renders twice: an outer copy (`outsideColor`) spans from the bar's
 * leading edge to the chart's opposite edge so long labels overflow the bar
 * up to the chart bounds; a wrapper at the bar's bounds clips an inner copy
 * (`insideColor`) via `overflow: hidden`. Where the two overlap, the inner
 * copy paints on top so the section *inside the bar* picks up the contrast
 * color while the section *outside the bar* keeps the neutral text color.
 *
 * When `isReversed` is true (the numerical X axis has `reversed` set, i.e.
 * RTL bars) the layout mirrors: bars grow right-to-left and the label anchors
 * at the bar's right edge (the zero-baseline side), allowing overflow toward
 * the left edge.
 */
export function HorizontalBarLabel({
  x,
  y,
  barWidth,
  barHeight,
  canvasWidth,
  isReversed,
  text,
  insideColor,
  outsideColor,
  truncationMode,
}: Readonly<HorizontalBarLabelProps>): ReactNode {
  // For horizontal bars the text's height sits across the bar's HEIGHT.
  if (barHeight < BAR_LABEL_MIN_SIZE) {
    return null;
  }

  const centerY = y + barHeight / 2;
  const availLength = isReversed
    ? x + barWidth - 2 * BAR_LABEL_PADDING
    : canvasWidth - x - 2 * BAR_LABEL_PADDING;
  if (availLength < BAR_LABEL_MIN_SIZE) {
    return null;
  }

  const truncated = truncateString(
    text,
    availLength,
    charsFontWidth12pixelsSize400Map,
    truncationMode,
  );

  const anchor: CSSProperties = isReversed
    ? { right: BAR_LABEL_PADDING, textAlign: 'right' }
    : { left: x + BAR_LABEL_PADDING };

  const outerStyle: CSSProperties = {
    ...anchor,
    top: centerY,
    transform: 'translateY(-50%)',
    width: availLength,
    color: outsideColor,
  };
  const wrapperStyle: CSSProperties = {
    left: x,
    top: y,
    width: barWidth,
    height: barHeight,
  };
  const innerStyle: CSSProperties = {
    ...(isReversed ? anchor : { left: BAR_LABEL_PADDING }),
    top: barHeight / 2,
    transform: 'translateY(-50%)',
    width: availLength,
    color: insideColor,
  };
  return (
    <Fragment>
      <span className={labelSpan} style={outerStyle}>
        {truncated}
      </span>
      <div className={labelClipWrapper} style={wrapperStyle}>
        <span className={labelSpan} style={innerStyle}>
          {truncated}
        </span>
      </div>
    </Fragment>
  );
}

HorizontalBarLabel['displayName'] = 'HorizontalBarLabel';
